import React, { FC, useCallback, useState } from "react"
import { Loader } from "dash-styled"
import { useDispatch } from "react-redux"
import { extendCustomerTrial } from "../../../../../../Actions/API/Backoffice/BackofficeActions"
import { useCustomer } from "../../../../../../Hooks/useCustomer"
import { hasValue } from "../../../../../../Models/Client/FetchState"
import { CustomerInfo } from "./CustomerInfo"
import { CustomerAccountAction } from "./CustomerAccountAction"
import { useBoolean } from "../../../../../../Hooks/useBoolean"
import { Confirm } from "../../../../../Common/Modal/Confirm"
import { UserPlan } from "../../../../../../Models/API/UserModel"
import { ExtendTrialModal } from "./ExtendTrialModal"
import { BackofficeDashboardBase } from "../../DashboardBase/BackofficeDashboardBase"
import { CustomerAppList } from "./CustomerAppList"

interface CustomerDetailsProps {
    username: string
}
export const CustomerDetails: FC<CustomerDetailsProps> = ({ username }) => {
    const dispatch = useDispatch()
    const [customerFetch, refreshBackofficeUser] = useCustomer(username)
    const [performingAction, setPerformingAction] = useState(false)
    const [trialExtendModal, showTrialExtendModal, hideTrialExtendModal] =
        useBoolean(false)
    const [
        convertFullUserModal,
        showConvertFullUserModal,
        hideConvertFullUserModal
    ] = useBoolean(false)

    const handleTrialExtend = useCallback(
        async (trialDays: number) => {
            if (hasValue(customerFetch)) {
                setPerformingAction(true)
                try {
                    await dispatch(
                        extendCustomerTrial(
                            customerFetch.value.username,
                            trialDays
                        )
                    )
                    refreshBackofficeUser()
                } finally {
                    setPerformingAction(false)
                    hideTrialExtendModal()
                }
            }
        },
        [customerFetch, dispatch, hideTrialExtendModal, refreshBackofficeUser]
    )

    const handleConvertFullUser = useCallback(async () => {
        if (hasValue(customerFetch)) {
            setPerformingAction(true)
            try {
                await dispatch(
                    extendCustomerTrial(
                        customerFetch.value.username,
                        undefined,
                        true
                    )
                )
                refreshBackofficeUser()
            } finally {
                setPerformingAction(false)
                hideConvertFullUserModal()
            }
        }
    }, [
        customerFetch,
        dispatch,
        hideConvertFullUserModal,
        refreshBackofficeUser
    ])

    if (!hasValue(customerFetch)) {
        return <Loader testId="backoffice-customer-loader" />
    }

    return (
        <BackofficeDashboardBase
            title={customerFetch.value.email}
            breadcrumbs={[
                "Sales backoffice",
                "Customer search",
                "Customer detail view"
            ]}
            fixedContentWidth={false}
        >
            <div className="flex flex-col overflow-hidden">
                <div className="flex space-x-2">
                    <CustomerInfo customer={customerFetch.value} />
                    {customerFetch.value.plan === UserPlan.FREEMIUM && (
                        <CustomerAccountAction
                            onConvertToFullUser={showConvertFullUserModal}
                            onTrialExtend={showTrialExtendModal}
                        />
                    )}
                </div>
                <CustomerAppList customer={customerFetch.value} />
                {convertFullUserModal && (
                    <Confirm
                        title="Confirm full user"
                        description="Cannot revert this change."
                        yesLabel="Ok"
                        isProcessing={performingAction}
                        onClickYes={handleConvertFullUser}
                        onClose={hideConvertFullUserModal}
                    />
                )}
                {trialExtendModal && (
                    <ExtendTrialModal
                        onClose={hideTrialExtendModal}
                        onSubmit={handleTrialExtend}
                        isProcessing={performingAction}
                        createdDate={customerFetch.value.created}
                        currentTrialDays={customerFetch.value.trialDays ?? 7}
                    />
                )}
            </div>
        </BackofficeDashboardBase>
    )
}
